import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import TrialCountdown from "@/components/TrialCountdown";
import FeaturePanel from "@/components/FeaturePanel";

export default function Signup() {
  const { signup } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ company_name: "", name: "", email: "", password: "" });
  const [acceptedTos, setAcceptedTos] = useState(false);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const handle = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!acceptedTos) {
      setErr("Please accept the Terms of Service and Privacy Policy to continue.");
      return;
    }
    setLoading(true);
    try {
      const u = await signup(form.company_name, form.name, form.email, form.password, true);
      toast.success(`Welcome ${u.name}! Add a card to start your 30-day free trial.`);
      navigate("/dashboard/billing");
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="sticky top-0 z-50"><TrialCountdown ctaLabel="Start now" /></div>
      <div className="min-h-screen flex flex-col lg:flex-row bg-white text-[#0B0B12]">
      <div className="lg:w-1/2 flex items-center justify-center p-8 lg:p-16">
        <div className="w-full max-w-md">
          <Link to="/" className="inline-flex items-center gap-2 mb-10" data-testid="signup-brand">
            <img src="/app-icon.png" alt="SalesArena HQ" className="w-9 h-9 rounded-sm object-cover" data-testid="signup-brand-logo" />
            <span className="font-logo text-xl whitespace-nowrap text-[#0B0B12]">SALES<span className="text-[#1E4FFF]">ARENA HQ</span></span>
          </Link>
          <h1 className="font-display text-4xl lg:text-5xl mb-2 text-[#0B0B12]">CREATE YOUR ARENA.</h1>
          <p className="text-[#4B5563] mb-8">30-day free trial. Cancel anytime before it ends and you won't be charged.</p>

          <form onSubmit={onSubmit} className="space-y-4">
            <div>
              <Label htmlFor="company" className="font-label text-[#4B5563]">Company name</Label>
              <Input
                id="company"
                required
                value={form.company_name}
                onChange={handle("company_name")}
                placeholder="Acme Sales Co"
                className="mt-2 bg-white border-[#D5D9E2] h-12 rounded-sm text-[#0B0B12] placeholder:text-[#9AA1AD] focus-visible:ring-[#1E4FFF]"
                data-testid="signup-company-input"
              />
            </div>
            <div>
              <Label htmlFor="name" className="font-label text-[#4B5563]">Your name</Label>
              <Input
                id="name"
                required
                value={form.name}
                onChange={handle("name")}
                placeholder="Jane Doe"
                className="mt-2 bg-white border-[#D5D9E2] h-12 rounded-sm text-[#0B0B12] placeholder:text-[#9AA1AD] focus-visible:ring-[#1E4FFF]"
                data-testid="signup-name-input"
              />
            </div>
            <div>
              <Label htmlFor="email" className="font-label text-[#4B5563]">Work email</Label>
              <Input
                id="email"
                type="email"
                required
                value={form.email}
                onChange={handle("email")}
                placeholder="you@company.com"
                className="mt-2 bg-white border-[#D5D9E2] h-12 rounded-sm text-[#0B0B12] placeholder:text-[#9AA1AD] focus-visible:ring-[#1E4FFF]"
                data-testid="signup-email-input"
              />
            </div>
            <div>
              <Label htmlFor="password" className="font-label text-[#4B5563]">Password (min 8)</Label>
              <Input
                id="password"
                type="password"
                required
                minLength={8}
                value={form.password}
                onChange={handle("password")}
                placeholder="••••••••"
                className="mt-2 bg-white border-[#D5D9E2] h-12 rounded-sm text-[#0B0B12] placeholder:text-[#9AA1AD] focus-visible:ring-[#1E4FFF]"
                data-testid="signup-password-input"
              />
            </div>
            {err && (
              <div className="text-sm text-[#E13C32] bg-[#E13C32]/10 border border-[#E13C32]/30 p-3 rounded-sm" data-testid="signup-error">
                {err}
              </div>
            )}
            <div className="flex items-start gap-3 pt-1" data-testid="signup-tos-container">
              <Checkbox
                id="tos"
                checked={acceptedTos}
                onCheckedChange={(v) => setAcceptedTos(v === true)}
                className="mt-0.5 border-[#D5D9E2] data-[state=checked]:bg-[#1E4FFF] data-[state=checked]:border-[#1E4FFF]"
                data-testid="signup-tos-checkbox"
              />
              <label htmlFor="tos" className="text-xs text-[#4B5563] leading-relaxed cursor-pointer select-none" data-testid="signup-legal-notice">
                I have read and agree to the{" "}
                <Link to="/terms" target="_blank" className="text-[#1E4FFF] hover:underline" data-testid="signup-terms-link" onClick={(e) => e.stopPropagation()}>
                  Terms of Service
                </Link>
                {", "}
                <Link to="/privacy" target="_blank" className="text-[#1E4FFF] hover:underline" data-testid="signup-privacy-link" onClick={(e) => e.stopPropagation()}>
                  Privacy Policy
                </Link>
                {", and "}
                <Link to="/refund-policy" target="_blank" className="text-[#1E4FFF] hover:underline" data-testid="signup-refund-link" onClick={(e) => e.stopPropagation()}>
                  Refund Policy
                </Link>
                .
              </label>
            </div>
            <Button
              type="submit"
              disabled={loading || !acceptedTos}
              className="w-full bg-[#1E4FFF] hover:bg-[#1740D6] text-white font-label h-12 rounded-sm disabled:opacity-50"
              data-testid="signup-submit-button"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create account & start trial →"}
            </Button>
          </form>

          <p className="mt-6 text-sm text-[#4B5563]">
            Already on SalesArena HQ?{" "}
            <Link to="/login" className="text-[#1E4FFF] hover:underline" data-testid="signup-login-link">
              Log in
            </Link>
          </p>

          <div className="lg:hidden mt-10 pt-8 border-t border-[#EDEFF3]" data-testid="signup-feature-panel-mobile">
            <FeaturePanel />
          </div>
        </div>
      </div>

      <div className="hidden lg:flex lg:w-1/2 relative bg-[#F7F8FB] p-12 xl:p-14 flex-col justify-center overflow-y-auto max-h-screen border-l border-[#EDEFF3]">
        <FeaturePanel />
      </div>
    </div>
    </>
  );
}
